<script setup>
import { FilterMatchMode } from '@primevue/core/api';
import { useToast } from 'primevue/usetoast';
import { onMounted, ref, computed, reactive, getCurrentInstance } from 'vue';
import { router, useForm } from '@inertiajs/vue3';
import { watchDebounced } from '@vueuse/core';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import ProgressBar from 'primevue/progressbar';
import Chip from 'primevue/chip';
import Badge from 'primevue/badge';
import FileUpload from 'primevue/fileupload';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const confirm = useConfirm();

const props = defineProps({
    title: String,
    sites: Object,
    coins: Object,
    countries: Array,
    languages: Array,
    categories: Array,
    sellers: Array,
    types: Array,
    pendingCount: Number,
    filters: Object,
});

// Reactive filters
const searchFilters = reactive({
    search: props.filters?.search || '',
    category_id: props.filters?.category_id || null,
    country_id: props.filters?.country_id || null,
    language_id: props.filters?.language_id || null,
    status: props.filters?.status || null,
    da_from: props.filters?.da_from || null,
    da_to: props.filters?.da_to || null,
    dr_from: props.filters?.dr_from || null,
    dr_to: props.filters?.dr_to || null,
    traffic_from: props.filters?.traffic_from || null,
    traffic_to: props.filters?.traffic_to || null,
});

// DataTable setup
const dt = ref();
const selectedSites = ref();
const deleteSiteDialog = ref(false);
const deleteSitesDialog = ref(false);
const siteToDelete = ref({});

// Filter options
const statusOptions = [
    { label: $t('All'), value: null },
    { label: $t('Pending'), value: 'PENDING' },
    { label: $t('Enabled'), value: 'ENABLED' },
    { label: $t('Disabled'), value: 'DISABLED' },
];

// Import dialog
const importDialog = ref(false);
const importForm = useForm({
    file: null,
});

// Watch for filter changes with debounce
watchDebounced(() => ({ ...searchFilters }), (newFilters) => {
    fetchSites();
}, {
    debounce: 400,
    deep: true,
});

const fetchSites = () => {
    router.get(route('sites.index'), searchFilters, {
        preserveState: true,
        preserveScroll: true,
    });
};

const clearFilters = () => {
    Object.keys(searchFilters).forEach(key => {
        searchFilters[key] = Array.isArray(searchFilters[key]) ? [] : null;
    });
    searchFilters.search = '';
    fetchSites();
};

const exportSites = () => {
    window.open(route('sites.exportUrls'), '_blank');
};

const exportPending = () => {
    window.open(route('sites.exportPending'), '_blank');
};

const confirmDeleteSite = (site) => {
    siteToDelete.value = site;
    deleteSiteDialog.value = true;
};

const deleteSite = () => {
    router.delete(route('sites.destroy', siteToDelete.value.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Site deleted successfully',
                life: 3000
            });
            deleteSiteDialog.value = false;
            siteToDelete.value = {};
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete site',
                life: 5000
            });
        }
    });
};

const confirmDeleteSelected = () => {
    deleteSitesDialog.value = true;
};

const deleteSelectedSites = () => {
    // Implementation for bulk delete
    deleteSitesDialog.value = false;
    selectedSites.value = null;
};

const editSite = (site) => {
    router.visit(route('sites.edit', site.id));
};

const viewSite = (site) => {
    window.open(site.url, '_blank');
};

const importSites = () => {
    importForm.post(route('sites.import'), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Sites imported successfully',
                life: 3000
            });
            importDialog.value = false;
            importForm.reset();
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to import sites',
                life: 5000
            });
        }
    });
};

const getStatusSeverity = (status) => {
    switch (status) {
        case 'PENDING': return 'warn';
        case 'ENABLED': return 'success';
        case 'DISABLED': return 'danger';
        default: return 'info';
    }
};

const formatCurrency = (value, currency) => {
    if (!value || !currency) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
    }).format(value);
};

const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
};

// Computed properties
const sitesWithHighestSale = computed(() => {
    if (!props.sites?.data) return [];
    
    return props.sites.data.map(site => {
        let highest_sale = 0;
        let highest_sale_coin = 'USD';
        
        if (site.offers && site.offers.length > 0) {
            site.offers.forEach(offer => {
                if (offer.all_types) {
                    offer.all_types.forEach(type => {
                        if (type.pivot && type.pivot.sale > highest_sale) {
                            highest_sale = type.pivot.sale;
                            highest_sale_coin = type.pivot.sale_coin;
                        }
                    });
                }
            });
        }
        
        return {
            ...site,
            highest_sale,
            highest_sale_coin
        };
    });
});
</script>

<template>
    <AdminMainLayout>
        <!-- Header -->
        <div class="flex align-items-center justify-content-between mb-4">
            <div>
                <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ $t('Sites Management') }}</h1>
                <p class="text-gray-600">{{ $t('Manage your websites and their configurations') }}</p>
            </div>
            <div class="flex gap-2">
                <Button 
                    :label="$t('Import Sites')" 
                    icon="pi pi-upload" 
                    severity="secondary"
                    @click="importDialog = true" />
                <Button 
                    :label="$t('Export URLs')" 
                    icon="pi pi-download" 
                    severity="secondary"
                    @click="exportSites" />
                <Button 
                    :label="$t('Add Site')" 
                    icon="pi pi-plus" 
                    @click="$inertia.visit(route('sites.create'))" />
            </div>
        </div>

        <!-- Filters Card -->
        <Card class="mb-4">
            <template #content>
                <div class="formgrid grid">
                    <!-- Search -->
                    <div class="field col-12 md:col-3">
                        <InputText 
                            v-model="searchFilters.search" 
                            :placeholder="$t('Search sites...')" 
                            class="w-full"
                            style="padding-right: 2.5rem;"
                        >
                            <template #suffix>
                                <i class="pi pi-search"></i>
                            </template>
                        </InputText>
                    </div>

                    <!-- Status -->
                    <div class="field col-12 md:col-2">
                        <Select 
                            v-model="searchFilters.status" 
                            :options="statusOptions" 
                            optionLabel="label" 
                            optionValue="value"
                            :placeholder="$t('Status')" 
                            class="w-full"
                            :clearable="false" />
                    </div>

                    <!-- Category -->
                    <div class="field col-12 md:col-2">
                        <Select 
                            v-model="searchFilters.category_id" 
                            :options="categories" 
                            optionLabel="name" 
                            optionValue="id"
                            :placeholder="$t('Category')" 
                            class="w-full"
                            :clearable="true" />
                    </div>

                    <!-- Country -->
                    <div class="field col-12 md:col-2">
                        <Select 
                            v-model="searchFilters.country_id" 
                            :options="countries" 
                            optionLabel="name" 
                            optionValue="id"
                            :placeholder="$t('Country')" 
                            class="w-full"
                            :clearable="true" />
                    </div>

                    <!-- Language -->
                    <div class="field col-12 md:col-2">
                        <Select 
                            v-model="searchFilters.language_id" 
                            :options="languages" 
                            optionLabel="name" 
                            optionValue="id"
                            :placeholder="$t('Language')" 
                            class="w-full"
                            :clearable="true" />
                    </div>

                    <!-- Clear Filters -->
                    <div class="field col-12 md:col-1">
                        <Button 
                            :label="$t('Clear')" 
                            icon="pi pi-filter-slash" 
                            severity="secondary" 
                            @click="clearFilters"
                            class="w-full" />
                    </div>
                </div>

                <!-- DA Range -->
                <div class="formgrid grid mt-3">
                    <div class="field col-12 md:col-2">
                        <label class="font-semibold text-sm">{{ $t('DA Range') }}</label>
                        <div class="flex gap-2">
                            <InputText 
                                v-model="searchFilters.da_from" 
                                :placeholder="$t('From')" 
                                type="number" />
                            <InputText 
                                v-model="searchFilters.da_to" 
                                :placeholder="$t('To')" 
                                type="number" />
                        </div>
                    </div>

                    <div class="field col-12 md:col-2">
                        <label class="font-semibold text-sm">{{ $t('DR Range') }}</label>
                        <div class="flex gap-2">
                            <InputText 
                                v-model="searchFilters.dr_from" 
                                :placeholder="$t('From')" 
                                type="number" />
                            <InputText 
                                v-model="searchFilters.dr_to" 
                                :placeholder="$t('To')" 
                                type="number" />
                        </div>
                    </div>

                    <div class="field col-12 md:col-2">
                        <label class="font-semibold text-sm">{{ $t('Traffic Range') }}</label>
                        <div class="flex gap-2">
                            <InputText 
                                v-model="searchFilters.traffic_from" 
                                :placeholder="$t('From')" 
                                type="number" />
                            <InputText 
                                v-model="searchFilters.traffic_to" 
                                :placeholder="$t('To')" 
                                type="number" />
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- Pending Sites Alert -->
        <div v-if="pendingCount > 0" class="mb-4">
            <Card>
                <template #content>
                    <div class="flex align-items-center gap-3">
                        <i class="pi pi-exclamation-triangle text-orange-500 text-2xl"></i>
                        <div class="flex-1">
                            <h3 class="text-lg font-semibold">{{ $t('Pending Sites') }}</h3>
                            <p class="text-gray-600">{{ $t('You have {count} sites pending approval', { count: pendingCount }) }}</p>
                        </div>
                        <Button 
                            :label="$t('Export Pending')" 
                            icon="pi pi-download" 
                            severity="warn"
                            @click="exportPending" />
                    </div>
                </template>
            </Card>
        </div>

        <!-- Main Data Table -->
        <Card>
            <template #content>
                <DataTable 
                    ref="dt"
                    v-model:selection="selectedSites"
                    :value="sitesWithHighestSale" 
                    dataKey="id"
                    paginator 
                    :rows="25"
                    :rowsPerPageOptions="[10, 25, 50, 100]"
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    :currentPageReportTemplate="`${$t('Showing')} {first} ${$t('to')} {last} ${$t('of')} {totalRecords} ${$t('sites')}`"
                    :globalFilterFields="['url', 'category.name', 'country.name', 'language.name']"
                    responsiveLayout="scroll"
                    class="p-datatable-sm">
                    
                    <template #header>
                        <div class="flex align-items-center justify-content-between">
                            <h2 class="text-xl font-semibold">{{ $t('Sites') }}</h2>
                            <div class="flex gap-2">
                                <Button 
                                    v-if="selectedSites && selectedSites.length > 0"
                                    :label="$t('Delete Selected')" 
                                    icon="pi pi-trash" 
                                    severity="danger" 
                                    @click="confirmDeleteSelected" />
                            </div>
                        </div>
                    </template>

                    <Column selectionMode="multiple" style="width: 3rem" :exportable="false"></Column>

                    <!-- URL Column -->
                    <Column field="url" :header="$t('URL')" sortable style="min-width: 200px">
                        <template #body="slotProps">
                            <div class="flex align-items-center gap-2">
                                <i class="pi pi-globe text-gray-500"></i>
                                <a :href="slotProps.data.url" target="_blank" class="text-primary-600 hover:text-primary-800 font-medium">
                                    {{ slotProps.data.url.replace(/^https?:\/\/(www\.)?/, '') }}
                                </a>
                            </div>
                        </template>
                    </Column>

                    <!-- Status Column -->
                    <Column field="status" :header="$t('Status')" sortable style="width: 120px">
                        <template #body="slotProps">
                            <Tag 
                                :value="slotProps.data.formatted_status || slotProps.data.status" 
                                :severity="getStatusSeverity(slotProps.data.status)" />
                        </template>
                    </Column>

                    <!-- Category Column -->
                    <Column field="category.name" :header="$t('Category')" sortable style="width: 150px">
                        <template #body="slotProps">
                            <Chip v-if="slotProps.data.category" :label="slotProps.data.category.name" class="text-xs" />
                            <span v-else class="text-gray-400">-</span>
                        </template>
                    </Column>

                    <!-- Metrics Column -->
                    <Column :header="$t('Metrics')" style="width: 150px">
                        <template #body="slotProps">
                            <div class="flex flex-column gap-1">
                                <div class="flex gap-2">
                                    <Badge :value="`DA: ${slotProps.data.da || 0}`" severity="info" />
                                    <Badge :value="`DR: ${slotProps.data.dr || 0}`" severity="success" />
                                </div>
                                <Badge v-if="slotProps.data.traffic" :value="`Traffic: ${slotProps.data.traffic}`" severity="warn" />
                            </div>
                        </template>
                    </Column>

                    <!-- Location Column -->
                    <Column :header="$t('Location')" style="width: 150px">
                        <template #body="slotProps">
                            <div class="flex flex-column gap-1">
                                <span v-if="slotProps.data.country" class="text-sm">
                                    🌍 {{ slotProps.data.country.name }}
                                </span>
                                <span v-if="slotProps.data.language" class="text-sm">
                                    🗣️ {{ slotProps.data.language.name }}
                                </span>
                            </div>
                        </template>
                    </Column>

                    <!-- Price Column -->
                    <Column :header="$t('Highest Sale')" style="width: 120px">
                        <template #body="slotProps">
                            <div v-if="slotProps.data.highest_sale > 0" class="font-semibold text-green-600">
                                {{ formatCurrency(slotProps.data.highest_sale, slotProps.data.highest_sale_coin) }}
                            </div>
                            <span v-else class="text-gray-400">-</span>
                        </template>
                    </Column>

                    <!-- Last Posted Column -->
                    <Column field="last_posted" :header="$t('Last Posted')" sortable style="width: 120px">
                        <template #body="slotProps">
                            <span v-if="slotProps.data.last_posted">
                                {{ formatDate(slotProps.data.last_posted) }}
                            </span>
                            <span v-else class="text-gray-400">-</span>
                        </template>
                    </Column>

                    <!-- Actions Column -->
                    <Column :header="$t('Actions')" style="width: 120px" :exportable="false">
                        <template #body="slotProps">
                            <div class="flex gap-1">
                                <Button 
                                    icon="pi pi-pencil" 
                                    size="small" 
                                    severity="info"
                                    v-tooltip="$t('Edit')"
                                    @click="editSite(slotProps.data)" />
                                <Button 
                                    icon="pi pi-external-link" 
                                    size="small" 
                                    severity="help"
                                    v-tooltip="$t('Visit')"
                                    @click="viewSite(slotProps.data)" />
                                <Button 
                                    icon="pi pi-trash" 
                                    size="small" 
                                    severity="danger"
                                    v-tooltip="$t('Delete')"
                                    @click="confirmDeleteSite(slotProps.data)" />
                            </div>
                        </template>
                    </Column>

                    <template #empty>
                        <div class="text-center p-4">
                            <i class="pi pi-search text-4xl text-gray-400 mb-3"></i>
                            <p class="text-gray-600 text-lg">{{ $t('No sites found') }}</p>
                            <p class="text-gray-500">{{ $t('Try adjusting your search criteria') }}</p>
                        </div>
                    </template>
                </DataTable>
            </template>
        </Card>

        <!-- Import Dialog -->
        <Dialog v-model:visible="importDialog" modal :header="$t('Import Sites')" :style="{width: '450px'}">
            <div class="flex flex-column gap-3">
                <p>{{ $t('Upload a CSV or Excel file with site data to import multiple sites at once.') }}</p>
                
                <FileUpload 
                    mode="basic" 
                    name="file"
                    accept=".csv,.xlsx"
                    :maxFileSize="10000000"
                    :customUpload="true"
                    @select="importForm.file = $event.files[0]"
                    @clear="importForm.file = null"
                    chooseLabel="Choose File" />
            </div>
            
            <template #footer>
                <Button :label="$t('Cancel')" icon="pi pi-times" severity="secondary" @click="importDialog = false" />
                <Button :label="$t('Import')" icon="pi pi-check" @click="importSites" :loading="importForm.processing" />
            </template>
        </Dialog>

        <!-- Delete Site Dialog -->
        <Dialog v-model:visible="deleteSiteDialog" modal :header="$t('Confirm Delete')" :style="{width: '450px'}">
            <div class="flex align-items-center gap-3 mb-3">
                <i class="pi pi-exclamation-triangle text-orange-500" style="font-size: 2rem"></i>
                <span>{{ $t('Are you sure you want to delete this site?') }}</span>
            </div>
            <p><strong>{{ siteToDelete.url }}</strong></p>
            
            <template #footer>
                <Button :label="$t('Cancel')" icon="pi pi-times" severity="secondary" @click="deleteSiteDialog = false" />
                <Button :label="$t('Delete')" icon="pi pi-check" severity="danger" @click="deleteSite" />
            </template>
        </Dialog>

        <!-- Delete Sites Dialog -->
        <Dialog v-model:visible="deleteSitesDialog" modal :header="$t('Confirm Delete')" :style="{width: '450px'}">
            <div class="flex align-items-center gap-3 mb-3">
                <i class="pi pi-exclamation-triangle text-orange-500" style="font-size: 2rem"></i>
                <span>{{ $t('Are you sure you want to delete the selected sites?') }}</span>
            </div>
            
            <template #footer>
                <Button :label="$t('Cancel')" icon="pi pi-times" severity="secondary" @click="deleteSitesDialog = false" />
                <Button :label="$t('Delete')" icon="pi pi-check" severity="danger" @click="deleteSelectedSites" />
            </template>
        </Dialog>

        <ConfirmDialog />
    </AdminMainLayout>
</template>

<style scoped>
.field {
    margin-bottom: 1rem;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
}
</style>